import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * ⌘K command palette primitive (cmdk inside a Radix dialog). PR2 wires
 * this into the Shell with navigation + entity-search sources; the
 * primitive itself is source-agnostic and fully controlled.
 */
export interface CommandItem {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly keywords?: readonly string[];
  readonly onSelect: () => void;
}

export interface CommandGroup {
  readonly heading: string;
  readonly items: readonly CommandItem[];
}

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly groups: readonly CommandGroup[];
  readonly placeholder: string;
  readonly emptyText: ReactNode;
  readonly ariaLabel: string;
  readonly search?: string;
  readonly onSearchChange?: (value: string) => void;
  readonly footer?: ReactNode;
  readonly testId?: string;
}

export function CommandPalette({
  open,
  onOpenChange,
  groups,
  placeholder,
  emptyText,
  ariaLabel,
  search,
  onSearchChange,
  footer,
  testId,
}: Props): JSX.Element {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 animate-overlay-in" />
        <DialogPrimitive.Content
          data-testid={testId}
          aria-describedby={undefined}
          className="fixed left-1/2 top-[15%] z-50 w-[calc(100vw-32px)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-line bg-raised shadow-overlay animate-panel-in focus:outline-none"
        >
          <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>
          <Command label={ariaLabel} shouldFilter={onSearchChange === undefined}>
            <div className="flex items-center gap-2.5 border-b border-line-soft px-4">
              <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />
              <Command.Input
                value={search}
                onValueChange={onSearchChange}
                placeholder={placeholder}
                className="h-12 w-full bg-transparent text-body text-paper outline-none placeholder:text-paper-faint"
              />
            </div>
            <Command.List className="max-h-[360px] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-8 text-center text-dense text-paper-low">
                {emptyText}
              </Command.Empty>
              {groups.map((group) => (
                <Command.Group
                  key={group.heading}
                  heading={group.heading}
                  className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:text-paper-low"
                >
                  {group.items.map((item) => (
                    <Command.Item
                      key={item.id}
                      value={`${item.label} ${item.keywords?.join(' ') ?? ''}`}
                      onSelect={item.onSelect}
                      className="flex cursor-default select-none items-center justify-between gap-3 rounded px-2.5 py-2 text-dense text-paper-mid outline-none transition-colors duration-120 data-[selected=true]:bg-active data-[selected=true]:text-paper"
                    >
                      <span className="truncate">{item.label}</span>
                      {item.hint !== undefined ? (
                        <span className="shrink-0 font-mono text-micro text-paper-low">
                          {item.hint}
                        </span>
                      ) : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
            {footer !== undefined && footer !== null ? (
              <div className="border-t border-line-soft px-4 py-2.5 text-micro text-paper-low">
                {footer}
              </div>
            ) : null}
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
